import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  Activity,
  TimelineShiftWithActivities,
  Shift,
  WithPreparedAttributes,
  ActivityWithCategoryAndLocationsAndReportedDetails,
} from '@wilson/interfaces';
import { DropZoneEventThrottlerDirective } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  RemoveActivityFromUnassignedRegion,
  SetDemarcatorRange,
  ShiftTimelineInteractionsState,
  TimelineCreateNewShiftV2,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertActivityV2,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { COMMON_SHIFT_RELATIONS, ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';

import { ActivityDropZoneHelperService } from '../../components/activity-drop-zone/activity-drop-zone-helper.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Component({
  selector: 'wilson-activity-drop-zone-v2',
  imports: [CommonModule],
  template: `@if (demarcatorRange$ | async; as demarcatorRange) {
    <div
      [style.left.px]="demarcatorRange.left"
      [style.right.px]="demarcatorRange.right"
      class="activityDropZone"
    ></div>
  }`,
  styleUrls: ['./activity-drop-zone-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityDropZoneV2Component extends DropZoneEventThrottlerDirective {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  @Input() userId!: string | undefined;
  private mockShift!:
    | ({
        activities: ActivityWithCategoryAndLocationsAndReportedDetails[];
      } & Omit<TimelineShiftWithActivities, 'activities'>)
    | null;
  demarcatorRange$ = this.store.select(
    ShiftTimelineInteractionsState.getDemarcatorRange,
  );

  protected onDragOverHandler(): void {
    if (this.userId) {
      this.mockShift =
        this.activityDropZoneHelperService.createShiftWithActivity(this.userId);

      if (this.mockShift) {
        this.store.dispatch([
          new TimelineUpsertNewShiftV2({
            shift: this.mockShift,
          }),
          new TimelineUpsertActivityV2(
            this.mockShift.activities,
            this.mockShift.id,
          ),
        ]);
      }
    }
  }

  protected async onDragLeaveHandler(): Promise<void> {
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    if (this.userId && activity?.id) {
      this.store.dispatch([
        new TimelineRemoveActivityFromShiftV2(
          activity?.id,
          CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        ),
        new TimelineRemoveShiftFromUserV2({
          userId: this.userId,
          shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        }),
      ]);
    }
  }

  protected async onDropHandler(): Promise<void> {
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );
    const isAssignmentMode = await firstValueFrom(
      this.dragAndDropModeService.isAssignmentMode$,
    );

    await this.handleAssignActivity(activity);
    if (isAssignmentMode && activity?.shiftId) {
      this.activityDropZoneHelperService.handleAssignActivityToExistingShift(
        activity,
        activity.shiftId,
      );
    }
    this.store.dispatch(new SetDemarcatorRange([]));
  }

  private refreshUserShiftValidationsProductivityAndStays(): void {
    if (this.userId) {
      this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
        this.userId,
      ]);
    }
  }

  private async handleAssignActivity(activity: Activity | null): Promise<void> {
    if (this.userId && activity?.id) {
      const tempShiftId = crypto.randomUUID();
      const tempShift =
        this.activityDropZoneHelperService.createShiftWithActivity(this.userId);

      if (tempShift) {
        try {
          this.store.dispatch([
            new TimelineRemoveShiftFromUserV2({
              userId: this.userId,
              shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            }),
            new TimelineRemoveActivityFromShiftV2(
              activity?.id,
              CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            ),
          ]);
          this.store.dispatch([
            new TimelineCreateNewShiftV2(
              {
                ...tempShift,
                id: tempShiftId,
              },
              this.userId,
            ),
            new TimelineUpsertActivityV2(tempShift.activities, tempShiftId),
          ]);
          this.createNewViaDragAndDropService.addUpdatingId(tempShiftId);
          const shifts = await firstValueFrom(
            this.shiftsService.createShifts([tempShift]),
          );
          const updatedShift = await firstValueFrom(
            this.shiftsService.get<
              Shift &
                WithPreparedAttributes & {
                  id: string;
                }
            >(shifts[0].id as string, {
              relations: COMMON_SHIFT_RELATIONS,
            }),
          );
          this.store.dispatch([
            new TimelineRemoveShiftFromUserV2({
              userId: this.userId,
              shiftId: tempShiftId,
            }),
            new TimelineRemoveActivityFromShiftV2(activity?.id, tempShiftId),
            new TimelineCreateNewShiftV2(
              {
                ...updatedShift,
                id: updatedShift.id as string,
                isHighlighted: true,
              },
              this.userId,
            ),
            new TimelineUpsertActivityV2(
              [
                {
                  ...tempShift.activities[0],
                  shiftId: updatedShift.id,
                },
              ],

              updatedShift.id,
            ),
            new RemoveActivityFromUnassignedRegion(activity),
          ]);
          this.timelineCommonOperationsService.updateUsersAssignmentsState([
            this.userId,
          ]);
          this.refreshUserShiftValidationsProductivityAndStays();

          this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
            [this.userId],
          );

          this.createNewViaDragAndDropService.removeUpdatingId(tempShiftId);
          this.activityDropZoneHelperService.setActivity(null);
          this.activityDropZoneHelperService.setUserId(null);
        } catch {
          this.store.dispatch([
            new TimelineRemoveActivityFromShiftV2(activity?.id, tempShiftId),
            new TimelineRemoveShiftFromUserV2({
              userId: this.userId,
              shiftId: tempShiftId,
            }),
          ]);
          this.createNewViaDragAndDropService.removeUpdatingId(tempShiftId);
          this.nzMessageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
        }
      }
    }
  }
}
